// Version: 2.13
// v2.13: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import React, { useEffect, useState } from 'react';
import { authFetch } from './auth';

const DEFAULT_POLICY = { min_length: 12, block_common: true, lockout_attempts: 10, require_complexity: false, expiry_days: 0 };
const DEFAULT_MFA = { enabled: true, trusted_days: 30 };

export default function PasswordPolicy() {
  const [policy,  setPolicy]  = useState(DEFAULT_POLICY);
  const [mfa,     setMfa]     = useState(DEFAULT_MFA);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    Promise.all([
      authFetch('/api/config/password-policy').then(r => r.json()),
      authFetch('/api/config/mfa').then(r => r.json())
    ]).then(([p, m]) => { setPolicy(p); setMfa(m); setLoading(false); })
    .catch(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const [r1, r2] = await Promise.all([
        authFetch('/api/config/password-policy', { method: 'PUT', body: JSON.stringify(policy) }),
        authFetch('/api/config/mfa', { method: 'PUT', body: JSON.stringify(mfa) })
      ]);
      if (!r1.ok || !r2.ok) throw new Error('Save failed');
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="card"><span className="spinner" /></div>;

  return (
    <div className="card">
      <div className="users-table-title">Security Settings</div>

      <div className="policy-ncsc-note">
        <div className="policy-ncsc-badge">NCSC</div>
        <div>
          <div style={{fontWeight:600,fontSize:13,color:'var(--text)',marginBottom:3}}>Follows UK NCSC guidance</div>
          <div style={{fontSize:12,color:'var(--text-muted)',lineHeight:1.5}}>
            Complexity rules are off by default — NCSC research shows they encourage weak passwords. Length, lockout and MFA are more effective controls.{' '}
            <a href="https://www.ncsc.gov.uk/collection/passwords" target="_blank" rel="noreferrer" style={{color:'var(--accent)'}}>Read the guidance →</a>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} style={{display:'flex',flexDirection:'column',gap:16,marginTop:20}}>

        {/* MFA section */}
        <div style={{borderBottom:'1px solid var(--border)',paddingBottom:16}}>
          <div style={{fontSize:12,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:12}}>Multi-Factor Authentication</div>

          <label className="policy-toggle" style={{marginBottom:10}}>
            <input type="checkbox" checked={mfa.enabled}
              onChange={e => setMfa(m => ({...m, enabled: e.target.checked}))} />
            <div>
              <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>Enable MFA for all users</div>
              <div style={{fontSize:12,color:'var(--text-muted)'}}>Users must verify via email code on new devices. NCSC strongly recommends MFA for internet-facing systems.</div>
            </div>
          </label>

          {mfa.enabled && (
            <div>
              <label>Trusted device period (days)</label>
              <input type="number" min={1} max={90} value={mfa.trusted_days}
                onChange={e => setMfa(m => ({...m, trusted_days: parseInt(e.target.value)}))} />
              <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>Users won't be prompted again on the same browser for this many days</div>
            </div>
          )}
        </div>

        {/* Password policy section */}
        <div style={{fontSize:12,fontWeight:600,letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--text-muted)',marginBottom:4}}>Password Policy</div>

        <div>
          <label>Minimum Password Length</label>
          <input type="number" min={8} max={64} value={policy.min_length}
            onChange={e => setPolicy(p => ({...p, min_length: parseInt(e.target.value)}))} />
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>NCSC recommends at least 12 characters</div>
        </div>

        <div>
          <label>Account Lockout After Failed Attempts</label>
          <input type="number" min={3} max={50} value={policy.lockout_attempts}
            onChange={e => setPolicy(p => ({...p, lockout_attempts: parseInt(e.target.value)}))} />
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>NCSC recommends 10. Account locks for 5 minutes.</div>
        </div>

        <div>
          <label>Password Expiry (days, 0 = never)</label>
          <input type="number" min={0} max={365} value={policy.expiry_days}
            onChange={e => setPolicy(p => ({...p, expiry_days: parseInt(e.target.value)}))} />
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>NCSC recommends no expiry unless compromise is suspected</div>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          <label style={{marginBottom:0}}>Additional Controls</label>

          <label className="policy-toggle">
            <input type="checkbox" checked={policy.block_common}
              onChange={e => setPolicy(p => ({...p, block_common: e.target.checked}))} />
            <div>
              <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>Block common passwords</div>
              <div style={{fontSize:12,color:'var(--text-muted)'}}>Prevents use of the 100 most common passwords</div>
            </div>
          </label>

          <label className="policy-toggle">
            <input type="checkbox" checked={policy.require_complexity}
              onChange={e => setPolicy(p => ({...p, require_complexity: e.target.checked}))} />
            <div>
              <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>Require complexity <span style={{fontSize:11,color:'var(--amber)',fontWeight:400}}>(not recommended by NCSC)</span></div>
              <div style={{fontSize:12,color:'var(--text-muted)'}}>Enforce uppercase, number and symbol requirements</div>
            </div>
          </label>
        </div>

        {error && <div className="error-msg">{error}</div>}
        {saved && <div className="success-msg">Settings saved</div>}

        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? <span className="spinner" /> : 'Save Settings'}
        </button>
      </form>
    </div>
  );
}
